import { type KeyboardEvent, useLayoutEffect, useRef, useState } from "react";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ARROW_MOVES, getEditorKeyMove, NO_ARROW_MOVES } from "./editorKeys";
import type { EditorProps } from "./editorProps";
import styles from "./editors.module.scss";

/** `isReplacing` also makes arrow keys commit and move, as in Excel's Enter mode. */
export interface InlineEditorProps extends EditorProps {
    align: "start" | "end";
    inputMode?: "text" | "decimal";
    placeholder?: string;
}

/**
 * The text input every inline editor shares: Enter commits down, Tab right,
 * Escape reverts, and leaving the cell commits. On Enter, Tab, or an arrow
 * key a value that doesn't parse keeps the editor open with its message;
 * leaving the cell with one reverts it.
 */
export const InlineEditor = ({
    initialText,
    isReplacing,
    ariaLabel,
    align,
    inputMode = "text",
    placeholder,
    onCommit,
    onCancel,
}: InlineEditorProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [text, setText] = useState(initialText);
    const [error, setError] = useState<string | undefined>(undefined);

    useLayoutEffect(() => {
        const input = inputRef.current;
        input?.focus();
        input?.setSelectionRange(input.value.length, input.value.length);
    }, []);

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.nativeEvent.isComposing) {
            event.stopPropagation();
            return;
        }
        if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            onCancel();
            return;
        }
        const move = getEditorKeyMove(
            event,
            isReplacing ? ARROW_MOVES : NO_ARROW_MOVES,
        );
        if (move) {
            event.preventDefault();
            event.stopPropagation();
            setError(onCommit({ text, move }));
        }
    };

    return (
        <Tooltip content={error} open={error !== undefined} side="bottom">
            <input
                ref={inputRef}
                className={styles.input}
                data-align={align}
                data-dt-editor=""
                aria-label={ariaLabel}
                aria-invalid={error !== undefined}
                inputMode={inputMode}
                placeholder={placeholder}
                value={text}
                onChange={(event) => {
                    setText(event.target.value);
                    setError(undefined);
                }}
                onKeyDown={handleKeyDown}
                onBlur={() => {
                    // The window lost focus (alt-tab, devtools); the input gets it back on return.
                    if (!document.hasFocus()) {
                        return;
                    }
                    onCommit({ text, move: "blur" });
                }}
            />
        </Tooltip>
    );
};
